import Glide, { Anchors, Breakpoints, Images, Swipe } from '@glidejs/glide/dist/glide.modular.esm';
import { updateURLs } from '../url';
import requestIdleCallback from '../shims/requestIdleCallback';
import { yieldToMainThread } from '../yield';
import Logger from '../logging/logger';
import { CommonEventParameters, getCommonElementParameters, triggerTWCEvent, TWCEventName, TWCImageInteractionEvent } from '../analytics/twc/events';
import { captureException } from '../logging/logger';
import { clearElement } from '../dom/element';

// For source, see: https://github.com/glidejs/glide/blob/master/src/components/keyboard.js
const keyboard = function( Glide, Components, Events ) {
    const nodesNotAllowedToTriggerNavigation = [
        'INPUT',
        'TEXTAREA',
    ];

    const component = {
        mount: function() {
            if ( Glide.settings.keyboard ) {
                this.bind();
            }
        },
        bind: function() {
            document.addEventListener( 'keyup', this.press );
        },
        unbind: function() {
            document.removeEventListener( 'keyup', this.press );
        },
        press: function( event ) {
            if ( event.target && nodesNotAllowedToTriggerNavigation.includes( event.target.nodeName ) ) {
                return;
            }

            const perSwipe = Glide.settings.perSwipe;

            if ( event.code === 'ArrowRight' ) {
                Components.Run.make( Components.Direction.resolve( perSwipe + '>' ) );
            }

            if ( event.code === 'ArrowLeft' ) {
                Components.Run.make( Components.Direction.resolve( perSwipe + '<' ) );
            }
        },
    };

    Events.on( [ 'destroy', 'update' ], function() {
        component.unbind();
    } );

    Events.on( 'update', function() {
        component.mount();
    } );

    return component;
};

type SliderState = {
    pictures: Array<HTMLPictureElement>;
    activeSlide: number;
}

type Picture = {
    img: Image;
    source?: Source;
}

type Source = {
    media: string;
    srcSet: string;
}

type Image = {
    src: string;
    alt?: string;
    dataAttributes?: Record<string, string>;
}

type AIPropertyImage = {
    picture: Picture;
}

class PropertySlider {
    static updateEvents = [
        'twc:popup:close:after',
        'twc:popup:close-property-search-filters:after',
        'twc:tabs-show',
        'twc:popup:close-property-alert',
    ];

    root: HTMLElement;
    glideRoot: HTMLElement;
    options: any;
    glide: any;
    slidesContainer: HTMLElement;
    slides: HTMLCollectionOf<Element>;
    total: number;
    indices: HTMLCollectionOf<HTMLElement>;
    counter: HTMLElement;
    hasUpdateLinks: boolean;
    totals: HTMLCollectionOf<Element>;
    images: HTMLCollectionOf<HTMLImageElement>;
    sources: HTMLCollectionOf<HTMLSourceElement>;
    leftControl: HTMLButtonElement | null;
    rightControl: HTMLButtonElement | null;
    srcToggle: HTMLButtonElement | null;
    modalContainer: HTMLElement | null;
    reimagineControls?: NodeListOf<HTMLButtonElement>;
    fullscreenControls: NodeListOf<HTMLButtonElement>;
    maximised: boolean;
    components: any;
    commonEventParams: CommonEventParameters = {};
    stateBeforeAIActivation: SliderState | null = null;
    aiImgs: Array<AIPropertyImage> = [];
    aiImgsActive: boolean = false;
    aiImgActiveIndex: number = -1;

    constructor( el: HTMLElement ) {
        if ( !el ) {
            throw new Error( 'root element must be a valid Element or Node' );
        }

        this.root = el;
        this.glideRoot = this.root.querySelector( '.glide' ) as HTMLElement;

        if ( !this.glideRoot ) {
            throw new Error( 'failed to find child element with class "glide"' );
        }

        this.options = this.buildOptions();

        this.glide = new Glide( this.glideRoot, this.options );
        this.slidesContainer = this.root.querySelector( '.glide__slides' ) as HTMLElement;
        this.slides = this.root.getElementsByClassName( 'glide__slide' );
        this.total = this.slides.length;
        this.indices = this.root.getElementsByClassName( 'glide__counter--index' ) as HTMLCollectionOf<HTMLElement>;
        this.counter = this.root.querySelector( '.glide__counter' ) as HTMLElement;

        this.hasUpdateLinks = this.root.dataset.updateLinks === 'true';

        this.totals = this.root.getElementsByClassName( 'glide__counter--total' );
        this.images = this.slidesContainer.getElementsByTagName( 'img' );
        this.sources = this.slidesContainer.getElementsByTagName( 'source' );

        this.leftControl = this.root.querySelector( '.glide__arrow--left' );
        this.rightControl = this.root.querySelector( '.glide__arrow--right' );

        this.modalContainer = this.root.closest( '.popup-property' ) as HTMLElement | null;

        this.commonEventParams = getCommonElementParameters( this.glideRoot );

        const isMainSlider = this.root.classList.contains( 'twc__property--slider-primary' );

        if ( isMainSlider && this.modalContainer ) {
            this.reimagineControls = this.modalContainer.querySelectorAll( 'button[data-action="reimagine-property"]' );
        }

        this.srcToggle = this.root.querySelector( '.glide__srctoggle' );

        if ( isMainSlider && this.srcToggle ) {
            this.srcToggle.addEventListener( 'click', this.onSrcToggleClick.bind( this ) );
        }

        if ( this.root.dataset.reimaginedImgs ) {
            this.aiImgs = JSON.parse( this.root.dataset.reimaginedImgs || '[]' ) as Array<AIPropertyImage>;
        }

        if ( this.aiImgs && this.aiImgs.length > 0 ) {
            document.addEventListener( 'twc:ai-photos:activate', this.activateAIImages.bind( this ) );
            document.addEventListener( 'twc:ai-photos:deactivate', this.deactivateAIImages.bind( this ) );
        }

        this.fullscreenControls = this.root.querySelectorAll( '.glide__fullscreen--control' ) as NodeListOf<HTMLButtonElement>;
        this.maximised = false;

        this.components = {
            'Anchors': Anchors,
            'Images': Images,
            'Breakpoints': Breakpoints,
        };

        if ( this.slides.length > 1 ) {
            this.components[ 'Swipe' ] = Swipe;
        }

        const keyboardSupported = ( this.root.dataset.keyboard && this.root.dataset.keyboard === 'true' );

        if ( this.slides.length > 1 && isMainSlider && keyboardSupported ) {
            this.components[ 'Keyboard' ] = keyboard;
        }

        if ( this.leftControl ) {
            this.leftControl.addEventListener( 'click', this.goLeft.bind( this ) );
        }

        if ( this.rightControl ) {
            this.rightControl.addEventListener( 'click', this.goRight.bind( this ) );
        }

        this.fullscreen = this.fullscreen.bind( this );
        this.root.querySelectorAll( '.glide__fullscreen--control' ).forEach( ( btn ) => {
            btn.addEventListener( 'click', this.fullscreen );
        } );

        this.update = this.update.bind( this );
        this.destoyAndReMake = this.destoyAndReMake.bind( this );
        this.handleUpdateEvent = this.handleUpdateEvent.bind( this );

        for ( let eventIndex = 0; eventIndex < PropertySlider.updateEvents.length; eventIndex++ ) {
            window.addEventListener( PropertySlider.updateEvents[ eventIndex ], this.handleUpdateEvent );
        }

        if ( window.screen && window.screen.orientation ) {
            screen.orientation.addEventListener( 'change', this.destoyAndReMake );
        }

        this.initEvents();

        this.glide.mount( this.components );

        el.classList.add( 'twc__property--slider-initialised' );

        if ( isMainSlider ) {
            this.root.dispatchEvent( new CustomEvent( 'twc:slider:init', {
                bubbles: true,
            } ) );
        }
    }

    goLeft( event: any ) {
        this.glide.go( ( event.target as HTMLElement ).dataset.glideDir );
    }

    goRight( event: any ) {
        this.glide.go( ( event.target as HTMLElement ).dataset.glideDir );
    }

    initEvents() {
        this.glide.on( 'run.before', ( event ) => {
            const isForward = event.direction === '>';

            this.preloadForEvent( this.glide.index, isForward );

            let nextIndex = 0;

            if ( isForward ) {
                nextIndex = this.glide.index + 1;

                if ( nextIndex >= this.total ) {
                    nextIndex = 0;
                }
            } else {
                nextIndex = this.glide.index - 1;

                if ( nextIndex < 0 ) {
                    nextIndex = this.total - 1;
                }
            }

            this.preloadForEvent( nextIndex, isForward );
        } );

        this.glide.on( 'run.after', async ( event ) => {
            if ( !this.glide ) {
                return;
            }

            this.updateIndices();

            await yieldToMainThread();

            this.updateControls();

            await yieldToMainThread();

            if ( this.aiImgsActive ) {
                this.updateAIImageState();
            }

            if ( this.hasUpdateLinks ) {
                this.updateLinks();
                await yieldToMainThread();
            }

            let direction = 'next';

            if ( event.direction === '<' ) {
                direction = 'previous';
            }

            let imgType = 'img';

            if ( this.aiImgsActive ) {
                imgType = 'ai_img';
            }

            const action = `view_${direction}_${imgType}`;

            const activePicture = this.getActivePicture();

            if ( activePicture ) {
                triggerTWCEvent<TWCImageInteractionEvent>( TWCEventName.ImageInteraction, {
                    ...this.commonEventParams,
                    action: action,
                    index: parseInt( activePicture.dataset.index as string ) + 1,
                } );
            }
        } );

        this.glide.on( 'build.after', () => {
            if ( !this.glide ) {
                return;
            }

            this.updateControls();
            this.updateIndices();
        } );

        this.glideRoot.querySelectorAll<HTMLImageElement>( '.glide__slide img' ).forEach( ( loadable ) => {
            const slide = loadable.closest( '.glide__slide' ) as HTMLElement;

            if ( !slide ) {
                return;
            }

            if ( loadable.loading === 'eager' || ( loadable as any ).loadable === 'auto' ) {
                if ( document.readyState === 'complete' ) {
                    slide.classList.remove( 'glide__slide--loading' );
                } else {
                    document.addEventListener( 'load', () => slide.classList.remove( 'glide__slide--loading' ) );
                }
                return;
            }

            if ( loadable.loading === 'lazy' && !loadable.complete && this.options.perView !== 1 ) {
                slide.classList.add( 'glide__slide--loading' );
            }

            if ( loadable.loading === 'lazy' && ( this.options.perView === 1 || loadable.complete ) ) {
                slide.classList.remove( 'glide__slide--loading' );
            }

            loadable.addEventListener( 'load', () => window.setTimeout( () => slide.classList.remove( 'glide__slide--loading' ), 200 ) );
        } );
    }

    getActivePicture(): HTMLPictureElement {
        const slide = Array.from( this.slides ).find( ( slide: Element ) => {
            return slide.classList.contains( 'glide__slide--active' );
        } ) as HTMLPictureElement;

        return slide.querySelector<HTMLPictureElement>( 'picture' ) as HTMLPictureElement;
    }

    handleUpdateEvent( event: Event ) {
        if ( !this.glide ) {
            return;
        }

        requestIdleCallback( async () => {
            if ( this.glide ) {
                Logger.info( '[slider] updating due to event', event );
                this.update();
            }
        } );
    }

    onSrcToggleClick( event: Event ): void {
        if ( !this.srcToggle ) {
            return;
        }

        let img: HTMLImageElement | null = null;

        for ( let i = 0; i < this.images.length; i++ ) {
            if ( !this.images[ i ].closest( '.glide__slide--active' ) ) {
                continue;
            }

            if ( !this.images[ i ].dataset.altSrc ) {
                continue;
            }

            img = this.images[ i ];
            break;
        }

        if ( !img || !img.parentElement || !img.dataset.altSrc ) {
            return;
        }

        const parentPictureElemenet = img.parentElement;

        const src = img.dataset.src || img.src;

        const newImg = document.createElement<'img'>( 'img' );
        newImg.src = img.dataset.altSrc;
        newImg.dataset.altSrc = src;

        newImg.dataset.visibilityTrackingContainer = img.dataset.visibilityTrackingContainer;
        newImg.dataset.visibilityTrackingId = newImg.src;

        let trackingGroup = '';

        if ( ( img.dataset.showingAlt || '' ) === 'true' ) {
            newImg.dataset.showingAlt = 'false';
            newImg.classList.add( 'contained' );
            trackingGroup = 'ai-photos';
        } else {
            newImg.dataset.showingAlt = 'true';
            trackingGroup = 'original-ai-photos';
        }

        const trackingTags = JSON.parse( img.dataset.visibilityTrackingTags || '{}' );
        trackingTags[ 'group' ] = trackingGroup;
        trackingTags[ 'index' ] = parseInt( parentPictureElemenet.dataset.index as string );

        newImg.dataset.visibilityTrackingTags = JSON.stringify( trackingTags );

        img.insertAdjacentElement( 'afterend', newImg );

        img.remove();

        this.srcToggle.classList.toggle( 'glide__srctoggle--before' );
        this.srcToggle.classList.toggle( 'glide__srctoggle--after' );

        this.root.dispatchEvent( new CustomEvent( 'twc:ai-photos:toggle', { bubbles: true } ) );
    }

    updateAIImageState() {
        if ( this.srcToggle ) {
            this.srcToggle.classList.add( 'glide__srctoggle--before' );
            this.srcToggle.classList.remove( 'glide__srctoggle--after' );
        }

        for ( let i = 0; i < this.images.length; i++ ) {
            if ( this.images[ i ].closest( '.glide__slide--active' ) ) {
                continue;
            }

            if ( this.images[ i ].dataset.showingAlt === 'true' ) {
                this.images[ i ].dataset.showingAlt = 'false';

                const newAltSrc = this.images[ i ].src;
                const newSrc = this.images[ i ].dataset.altSrc as string;

                this.images[ i ].src = newSrc;
                this.images[ i ].dataset.altSrc = newAltSrc;
            }
        }

        const state = this.state();

        this.aiImgActiveIndex = state.index;
    }

    deactivateAIImages( event: Event ) {
        const state = this.stateBeforeAIActivation;

        if ( !state ) {
            return;
        }

        Logger.info( '[slider] deactivating ai photos', event );

        if ( this.srcToggle ) {
            this.srcToggle.classList.add( 'glide__srctoggle--hidden', 'glide__srctoggle--before' );
            this.srcToggle.classList.remove( 'glide__srctoggle--after' );
        }

        const slideElements = state.pictures.map( ( picture: HTMLPictureElement ) => {
            picture.dataset.visibilityTrackingObserved = 'false';

            const newSlideElement = document.createElement<'li'>( 'li' );
            newSlideElement.className = 'glide__slide glide__fullscreen--control disable-child-touch';

            newSlideElement.insertAdjacentHTML( 'beforeend', `
                <div class="glide__slide-inner">
                    ${picture.outerHTML}
                </div>
            ` );

            return newSlideElement;
        } );

        this.glide.destroy();

        while ( this.slidesContainer.lastElementChild ) {
            this.slidesContainer.lastElementChild.remove();
        }

        clearElement( this.slidesContainer );

        slideElements.forEach( ( element: HTMLLIElement ) => {
            this.slidesContainer.append( element );
        } );

        this.total = state.pictures.length;

        this.slides = this.root.getElementsByClassName( 'glide__slide' );
        this.images = this.slidesContainer.getElementsByTagName( 'img' );
        this.sources = this.slidesContainer.getElementsByTagName( 'source' );

        this.updateTotals( this.images.length );

        this.glide = new Glide( this.glideRoot, this.buildOptions( {
            startAt: state.activeSlide,
        } ) );

        this.initEvents();

        this.glide.mount( this.components );

        this.stateBeforeAIActivation = null;

        this.aiImgsActive = false;

        this.root.dispatchEvent( new CustomEvent( 'twc:ai-photos:deactivated', { bubbles: true } ) );
    }

    activateAIImages( event: Event ) {
        if ( !this.aiImgs || this.aiImgs.length === 0 ) {
            return;
        }

        Logger.info( '[slider] activating ai photos', event );

        let startingIndex = 0;

        if ( this.aiImgActiveIndex >= 0 ) {
            startingIndex = this.aiImgActiveIndex;
        }

        triggerTWCEvent<TWCImageInteractionEvent>( TWCEventName.ImageInteraction, {
            ...this.commonEventParams,
            action: 'view_ai_photos',
            index: startingIndex,
        } );

        const state = this.state();

        const currentState: SliderState = {
            activeSlide: state.index,
            pictures: [],
        };

        for ( let i = 0; i < this.slides.length; i++ ) {
            const slide = this.slides[ i ];

            if ( slide.classList.contains( 'glide__slide--clone' ) ) {
                continue;
            }

            const picture = slide.querySelector( 'picture' ) as HTMLPictureElement;

            currentState.pictures.push( picture.cloneNode( true ) as HTMLPictureElement );
        }

        this.stateBeforeAIActivation = currentState;

        const slideElements = this.aiImgs.map( ( entry: AIPropertyImage, index: number ) => {
            const newSlideElement = document.createElement<'li'>( 'li' );
            newSlideElement.className = 'glide__slide glide__fullscreen--control disable-child-touch';

            const picture = document.createElement<'picture'>( 'picture' );
            picture.dataset.index = index.toString();

            const img = document.createElement<'img'>( 'img' );
            img.fetchPriority = 'high';
            img.alt = entry.picture.img.alt || '';
            img.classList.add( 'contained' );

            img.dataset.visibilityTrackingContainer = '.twc__property--slider';
            img.dataset.visibilityTrackingId = entry.picture.img.src;
            img.dataset.visibilityTrackingTags = JSON.stringify( {
                group: 'ai-photos',
                propertyId: this.modalContainer?.dataset.propertyId,
                clientId: this.modalContainer?.dataset.clientId,
                index: index,
            } );

            let srcImgID = '';

            if ( entry.picture.img.dataAttributes ) {
                for ( const [ key, value ] of Object.entries( entry.picture.img.dataAttributes ) ) {
                    if ( key === 'alt-img-id' ) {
                        srcImgID = value;
                    }

                    img.setAttribute( `data-${key}`, value );
                }
            }

            if ( srcImgID !== '' ) {
                const srcPicture = currentState.pictures.find( ( p: HTMLPictureElement ) => {
                    return p.dataset.photoId === srcImgID;
                } );

                const srcImg = srcPicture?.querySelector( 'img' );

                if ( srcImg ) {
                    img.dataset.altSrc = srcImg.dataset.src || srcImg.src;
                }
            }

            if ( index === startingIndex ) {
                img.src = entry.picture.img.src;
            } else {
                img.src = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
                img.dataset.src = entry.picture.img.src;
            }

            picture.append( img );

            if ( entry.picture.source ) {
                const source = document.createElement<'source'>( 'source' );
                source.media = entry.picture.source.media;
                source.srcset = entry.picture.source.srcSet;
                picture.append( source );
            }

            newSlideElement.insertAdjacentHTML( 'beforeend', `<div class="glide__slide-inner">${picture.outerHTML}</div>` );

            return newSlideElement;
        } );

        this.glide.destroy();

        while ( this.slidesContainer.lastElementChild ) {
            this.slidesContainer.lastElementChild.remove();
        }

        slideElements.forEach( ( element: HTMLLIElement ) => {
            this.slidesContainer.append( element );
        } );

        this.total = this.aiImgs.length;

        this.slides = this.root.getElementsByClassName( 'glide__slide' );
        this.images = this.slidesContainer.getElementsByTagName( 'img' );
        this.sources = this.slidesContainer.getElementsByTagName( 'source' );

        this.updateTotals( this.images.length );

        this.glide = new Glide( this.glideRoot, this.buildOptions( {
            startAt: startingIndex,
        } ) );

        this.initEvents();

        this.glide.mount( this.components );

        if ( this.srcToggle ) {
            this.srcToggle.classList.add( 'glide__srctoggle--before' );
            this.srcToggle.classList.remove( 'glide__srctoggle--hidden' );
        }

        this.aiImgsActive = true;

        this.root.dispatchEvent( new CustomEvent( 'twc:ai-photos:activated', { bubbles: true } ) );
    }

    preloadForEvent( currentGlideIndex: number, isForward: boolean ) {
        const preloadFor2ImgView = ( this.options.perView === 2 && window.innerWidth >= 768 );

        if ( this.glide.type === 'carousel' ) {
            if ( preloadFor2ImgView ) {
                throw new Error( 'at the moment carousel type slider will not work with > 1 images per view setting' );
            }

            let nextActiveImageIndex;
            const totalImagesWithClones = this.images.length;
            // because there are always 2 clones at the beginning (and at the end)
            const activeImageIndex = currentGlideIndex + 2;

            if ( isForward ) {
                nextActiveImageIndex = activeImageIndex + 1;
                // there will always be next (either real image or a clone)
                this.preload( nextActiveImageIndex );
                // if this is boundary we also have to load first real image
                if ( nextActiveImageIndex === totalImagesWithClones - 2 ) {
                    this.preload( 2 );
                }
            } else {
                nextActiveImageIndex = activeImageIndex - 1;
                // there will always be prev (either real image or a clone)
                this.preload( nextActiveImageIndex );
                // if this is boundary we also have to load last real image
                if ( nextActiveImageIndex === 1 ) {
                    this.preload( totalImagesWithClones - 3 );
                }
            }
        } else {
            let nextIndex = 0;
            if ( isForward ) {
                if ( preloadFor2ImgView ) {
                    nextIndex = currentGlideIndex + 2;
                } else {
                    nextIndex = currentGlideIndex + 1;
                }

                if ( nextIndex >= this.total ) {
                    nextIndex = 0;
                }
            } else {
                nextIndex = currentGlideIndex - 1;
                if ( nextIndex < 0 ) {
                    nextIndex = this.total - 1;
                }
            }
            this.preload( nextIndex );
        }
    }

    preload( index: number ) {
        const img = this.images[ index ];

        if ( !img ) {
            captureException( new Error( `image at index ${index} for ${this.options.type} not found, images length: ${this.images.length}` ) );
            return;
        }

        if ( index < this.sources.length && this.sources[ index ].hasAttribute( 'data-srcset' ) ) {
            const source = this.sources[ index ];
            source.srcset = source.dataset.srcset || '';
            source.removeAttribute( 'data-srcset' );
        }
        if ( ( img.hasAttribute( 'data-src' ) || img.loading === 'lazy' ) && !img.complete ) {
            const slide = img.closest( '.glide__slide' ) as HTMLElement;
            if ( slide ) {
                slide.classList.add( 'glide__slide--loading' );
            }
        }

        if ( img.hasAttribute( 'data-src' ) ) {
            img.src = img.dataset.src || '';
            img.removeAttribute( 'data-src' );
        }

        img.classList.add( 'loaded' );
    }

    updateTotals( newTotal: number ) {
        for ( let i = 0; i < this.totals.length; i++ ) {
            this.totals[ i ].innerHTML = newTotal.toString();
        }
    }

    updateIndices() {
        const state = this.state();
        const index = state.index;
        let offset = 1;

        if ( this.options.perView === 2 && window.innerWidth >= 768 ) {
            offset = 2;
        }

        const indexToUse = ( index + offset );

        for ( let i = 0; i < this.indices.length; i++ ) {
            this.indices[ i ].innerText = indexToUse.toString();
        }

        this.root.dataset.startAt = index.toString();
    }

    updateControls() {
        const state = this.state();

        let index = ( state.index + 1 );

        if ( this.options.perView === 2 ) {
            index += 1;
        }

        if ( this.options.type === 'slider' && this.leftControl && this.rightControl ) {
            this.leftControl.disabled = state.index === 0;
            this.rightControl.disabled = index === state.total;
        }

        if ( index === state.total ) {
            this.counter.classList.add( 'd-none' );
        }

        if ( index !== state.total ) {
            this.counter.classList.remove( 'd-none' );
        }
    }

    updateLinks() {
        const state = this.state();

        updateURLs( '.property-link', 'slide', ( state.index + 1 ).toString(), this.root.closest( '.twc__grid-item,.twc__map-search-property' ) as any );
    }

    async fullscreen( event ) {
        const handleESC = ( event: KeyboardEvent ) => {
            if ( event.key === 'Escape' ) {
                close();
            }
        };

        const close = async () => {
            document.documentElement.style.overflow = '';

            await yieldToMainThread();

            this.root.classList.remove( 'twc__property--slider-maximised' );
            this.root.classList.add( 'twc__property--slider-minimised' );

            await yieldToMainThread();

            if ( this.glide ) {
                this.glide.update();
            }

            document.removeEventListener( 'keyup', handleESC );
            this.maximised = false;
        };

        const open = async () => {
            this.root.classList.add( 'twc__property--slider-maximised' );
            this.root.classList.remove( 'twc__property--slider-minimised' );

            await yieldToMainThread();

            this.glide.update();

            document.addEventListener( 'keyup', handleESC );
            this.maximised = true;

            requestIdleCallback( () => {
                document.documentElement.style.overflow = 'hidden';
            }, { timeout: 50 }  );
        };

        let eventSuffix = '';

        if ( this.maximised ) {
            eventSuffix = 'minimise';
            await close();
        } else {
            eventSuffix = 'maximise';
            await open();
        }

        this.root.dispatchEvent( new CustomEvent( `twc:slider:${eventSuffix}`, {
            bubbles: true,
            detail: {
                el: this.root,
            },
        } ) );

        this.fullscreenControls.forEach( ( btn ) => {
            if ( !btn.title || !btn.dataset.altTitle ) {
                return;
            }

            const title = btn.title;
            btn.title = btn.dataset.altTitle;
            btn.dataset.altTitle = title;
        } );
    }

    state(): State {
        return {
            index: this.glide.index,
            total: this.total,
        };
    }

    async update() {
        this.options = this.buildOptions();
        this.glide.update( this.options );
        await yieldToMainThread();
    }

    destroy() {
        if ( this.glide !== null ) {
            this.glide.destroy();
            this.glide = null;

            for ( let i = 0; i < PropertySlider.updateEvents.length; i++ ) {
                window.removeEventListener( PropertySlider.updateEvents[ i ], this.handleUpdateEvent );
            }
        }

        if ( window.screen && window.screen.orientation ) {
            screen.orientation.removeEventListener( 'change', this.destoyAndReMake );
        }

        if ( this.leftControl ) {
            this.leftControl.removeEventListener( 'click', this.goLeft );
        }

        if ( this.rightControl ) {
            this.rightControl.removeEventListener( 'click', this.goRight );
        }
    }

    destoyAndReMake() {
        if ( this.glide ) {
            this.glide.destroy();
        }

        this.options = this.buildOptions( {
            startAt: this.root.dataset.startAt || 0,
        } );
        this.glide = new Glide( this.glideRoot, this.options );
        this.initEvents();
        this.glide.mount( this.components );
    }

    buildOptions( overrides?: any ) {
        const options = {
            rewind: false,
            type: this.root.dataset.type || 'slider',
            animationDuration: 300, // default = 400 (ms)
            swipeThreshold: 20, // default = 80 (px)
            gap: 0,
            startAt: 0,
            perView: parseInt( this.root.dataset.perView || '1' ) || 1,
            breakpoints: {
                767: {
                    perView: 1,
                },
            },
        };

        if ( window.innerWidth < 768 ) {
            options.perView = 1;
        }

        if ( !overrides || !overrides[ 'startAt' ] ) {
            options.startAt = parseInt( this.root.dataset.startAt || '0' ) || 0;
        }

        return Object.assign( {}, options, overrides || {} );
    }
}

type State = {
    index: number;
    total: number;
}

export default function initPropertySliders(): void {
    const observer = new IntersectionObserver( ( entries ) => {
        entries.forEach( ( entry ) => {
            if ( !entry.isIntersecting ) {
                entry.target.querySelectorAll( '.glide__slide--loading' ).forEach( ( slide ) => slide.classList.remove( 'glide__slide--loading' ) );
                return;
            }

            observer.unobserve( entry.target );

            requestIdleCallback( () => {
                entry.target[ 'slider' ] = new PropertySlider( entry.target as HTMLElement );
            }, { timeout: 100 } );
        } );
    } );

    const findObservables = ( event ) => {
        let container = document;

        if ( event.detail && event.detail.container ) {
            container = event.detail.container;
        }

        container.querySelectorAll<HTMLElement>( '.twc__property--slider' ).forEach( ( slider ) => {
            if ( slider.dataset.observed || !slider.querySelector( '.glide' ) ) {
                return;
            }

            observer.observe( slider );
            slider.dataset.observed = 'true';
        } );
    };

    // Do NOT use onReady or DOMContentLoaded as Glide needs
    // the first image to be loaded to initialise correctly
    window.addEventListener( 'load', findObservables );

    window.addEventListener( 'twc:newcontentloaded', findObservables );
}
